<script setup lang="ts">
import activitySm from "~/assets/icons/panel/sm/activity.svg"
import agentSm from "~/assets/icons/panel/sm/agent.svg"
import chatsSm from "~/assets/icons/panel/sm/chats.svg"
import detailsSm from "~/assets/icons/panel/sm/details.svg"
import toolsSm from "~/assets/icons/panel/sm/tools.svg"
import waterfallSm from "~/assets/icons/panel/sm/waterfall.svg"
import activityXs from "~/assets/icons/panel/xs/activity.svg"
import agentXs from "~/assets/icons/panel/xs/agent.svg"
import chatsXs from "~/assets/icons/panel/xs/chats.svg"
import detailsXs from "~/assets/icons/panel/xs/details.svg"
// import toolsXs from "~/assets/icons/panel/xs/tools.svg"
import waterfallXs from "~/assets/icons/panel/xs/waterfall.svg"
import { InterfacePanelState } from "~/classes/generated/interface"

const { state: panelState, showPanelAndPersist, hidePanelAndPersist } = usePanel()
const { state: mainViewState } = useMainView()

const isInAgentView = computed(() => mainViewState.value.state === "agent")
function showPanelAndClose(panel: InterfacePanelState, toggle: () => void) {
  showPanelAndPersist(panel)
  toggle()
}
function hidePanelAndClose(toggle: () => void) {
  hidePanelAndPersist()
  toggle()
}
</script>

<template>
  <div class="panel-pill">
    <dropdown
      anchor-x="right"
      anchor-y="top"
      flow-x="left"
      flow-y="up"
    >
      <template #default="{ toggle }">
        <nav-pill>
          <nav-pill-button
            class="panel-pill-button"
            @click="toggle"
          >
            <template v-if="panelState.visible && panelState.state === InterfacePanelState.Agent">
              <agent-sm class="icon" />
              <div>Agent</div>
            </template>
            <template v-else-if="panelState.visible && panelState.state === InterfacePanelState.Chats">
              <chats-sm class="icon" />
              <div>Chats</div>
            </template>
            <template v-else-if="panelState.visible && panelState.state === InterfacePanelState.Details">
              <details-sm class="icon" />
              <div>Details</div>
            </template>
            <template v-else-if="panelState.visible && panelState.state === InterfacePanelState.Waterfall">
              <waterfall-sm class="icon" />
              <div>Waterfall</div>
            </template>
            <template v-else-if="panelState.visible && panelState.state === InterfacePanelState.Activity">
              <activity-sm class="icon" />
              <div>Activity</div>
            </template>
            <template v-else>
              <tools-sm class="icon" />
              <div>Tools</div>
            </template>
          </nav-pill-button>
        </nav-pill>
      </template>
      <template #menu="{ toggle }">
        <dropdown-menu class="panel-dropdown">
          <menu-section>
            <div
              v-if="!isInAgentView"
              class="option"
              @click="showPanelAndClose(InterfacePanelState.Agent, toggle)"
            >
              <agent-xs class="icon" />
              <div>Agent</div>
            </div>
            <div class="option" @click="showPanelAndClose(InterfacePanelState.Chats, toggle)">
              <chats-xs class="icon" />
              <div>Chats</div>
            </div>
            <menu-separator />
            <div class="option" @click="showPanelAndClose(InterfacePanelState.Details, toggle)">
              <details-xs class="icon" />
              <div>Details</div>
            </div>
            <div
              v-if="!isInAgentView"
              class="option"
              @click="showPanelAndClose(InterfacePanelState.Waterfall, toggle)"
            >
              <waterfall-xs class="icon" />
              <div>Waterfall</div>
            </div>
            <div class="option" @click="showPanelAndClose(InterfacePanelState.Activity, toggle)">
              <activity-xs class="icon" />
              <div>Activity</div>
            </div>
          </menu-section>
          <template v-if="panelState.visible">
            <menu-separator />
            <menu-section>
              <div class="option" @click="hidePanelAndClose(toggle)">
                <icon-action type="cross" class="icon" />
                <div>Hide</div>
              </div>
            </menu-section>
          </template>
        </dropdown-menu>
      </template>
    </dropdown>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.panel-pill
  @apply absolute bottom-[#{$nav-gap-pills}] right-[#{$nav-gap-pills}] z-navigation-layer
  .icon
    @apply text-[#{$pill-t-default}]

.panel-pill-button
  @apply px-2.5
  .icon
    @apply -ml-1
  &:hover, &.selected
    .icon
      @apply text-[#{$pill-t-hovered}]

.panel-dropdown
  @apply mb-2 w-30

.option
  @apply flex items-center cursor-pointer t-menu-sm rounded-[#{$menu-br-item}] py-1 pr-1.5 pl-1 -my-1 -mx-1.5 gap-2
  &:hover
    @apply bg-[#{$menu-bg-item-hover}] text-[#{$global-t-default}]
  .icon
    @apply text-[#{$menu-viewmode-t-property}]
</style>
